/**
 * 스토리 원고 → 플레이 단계(step) 목록
 *
 * ── 원고 문법 (한 줄 = 화면 한 번 탭) ──────────────────────────
 *   # 장 제목                     장 제목 카드 (화면 전환)
 *   [분위기 밤] [음악 빗소리]       연출 지시. 한 줄에 여러 개 가능, 탭 없이 바로 적용
 *       분위기: 기본 밤 새벽 아침 낮 사무실 카페 방 비 노을
 *       음악:   빗소리 방 밤 카페 끔   (또는 테스트 데이터 sounds 에 넣은 mp3 이름)
 *       효과:   [두근] [흔들림] [암전] [쉼]
 *       조각:   [조각 이름] … [조각 끝]   (읽기 지도용) 이 구간에서 '공감' 하트를 누를 수 있음
 *       장면:   [장면 room]  (픽셀 모드) 위쪽 픽셀 화면을 바꿈. 이름은 src/story/pixel/scenes.js
 *       연출:   [연출 결제]  (픽셀 모드) 지금 장면 안의 작은 이벤트
 *   알림: 보낸이 | 내용             휴대폰 알림 카드
 *   “남의 말” / "남의 말"          따옴표로 시작 = 남의 말
 *   (속마음)                       괄호로 감싼 줄 = 속마음·감각
 *   그 외                          1인칭 독백
 *
 *   ? 질문                         행동 선택 (버튼)
 *   ?? 질문                        생각 선택 (떠다니는 말풍선)
 *   - 선택지 {습관+2, 습관2+1}      선택지와 점수. {습관} 만 쓰면 +1
 *       들여쓴 줄                   그 선택지를 골랐을 때만 나오는 반응 (여러 줄 가능)
 *
 *   @순서 파국화, 가면, …           (선택) 동점일 때 우선순위·그래프 순서
 *   @묶음 두려움: 평가, 들킴, …      (선택) 습관을 묶음으로 나눔. 결과는 첫 묶음에서 가장 높은 습관,
 *   @묶음 환상: 비법, 자격, …              나머지 묶음은 결과 페이지에 함께 표시
 *   // 메모                        무시되는 줄
 */

const SCORE_BLOCK = /\{([^}]*)\}\s*$/;
const DIRECTIVE = /\[([^\]]+)\]/g;
const EFFECTS = ["두근", "흔들림", "암전", "쉼"];

const parseScores = (raw) => {
  const scores = {};
  raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((part) => {
      const m = part.match(/^(.+?)\s*([+-]\s*\d+(?:\.\d+)?)?$/);
      if (!m) return;
      const name = m[1].trim();
      const value = m[2] ? Number(m[2].replace(/\s/g, "")) : 1;
      scores[name] = (scores[name] || 0) + value;
    });
  return scores;
};

const lineStep = (text) => {
  const t = text.trim();
  let m;
  if ((m = t.match(/^알림\s*[:：]\s*(.*)$/))) {
    const [from, ...rest] = m[1].split("|");
    return { type: "notify", from: from.trim(), text: rest.join("|").trim() };
  }
  if (/^[“"「『]/.test(t)) return { type: "line", style: "speech", text: t };
  if (/^\(.*\)$/.test(t) || /^（.*）$/.test(t)) {
    return { type: "line", style: "inner", text: t.slice(1, -1).trim() };
  }
  return { type: "line", style: "mono", text: t };
};

const directiveStep = (line) => {
  const tokens = [...line.matchAll(DIRECTIVE)].map((m) => m[1].trim());
  if (!tokens.length || line.replace(DIRECTIVE, "").trim()) return null;
  const step = { type: "direct", effects: [] };
  tokens.forEach((tok) => {
    const [head, ...rest] = tok.split(/\s+/);
    const arg = rest.join(" ");
    if (head === "분위기" && arg) step.mood = arg;
    else if (head === "음악" && arg) step.music = arg;
    else if (head === "조각" && arg) step.part = arg === "끝" ? "" : arg;
    else if (head === "장면" && arg) step.scene = arg;
    else if (head === "연출" && arg) (step.cues = step.cues || []).push(arg);
    else if (EFFECTS.includes(head)) step.effects.push(head);
  });
  return step;
};

export const parseScript = (script = "") => {
  const steps = [];
  const traitOrder = [];
  const groups = []; // [{ name, traits }]
  const seeTrait = (name) => {
    if (!traitOrder.includes(name)) traitOrder.push(name);
  };

  let choice = null; // 지금 모으는 선택 단계
  let option = null; // 지금 반응 줄을 모으는 선택지

  const closeChoice = () => {
    if (choice) steps.push(choice);
    choice = null;
    option = null;
  };

  script
    .replace(/\r\n/g, "\n")
    .split("\n")
    .forEach((raw) => {
      if (!raw.trim() || raw.trim().startsWith("//")) {
        // 빈 줄은 선택지 묶음을 끝내지 않음 (반응 줄 사이 빈 줄 허용)
        return;
      }
      const indented = /^\s+/.test(raw);
      const line = raw.trim();

      // 선택지 묶음 안
      if (choice) {
        if (line.startsWith("- ")) {
          const body = line.slice(2);
          const sm = body.match(SCORE_BLOCK);
          const scores = sm ? parseScores(sm[1]) : {};
          Object.keys(scores).forEach(seeTrait);
          option = {
            text: (sm ? body.replace(SCORE_BLOCK, "") : body).trim(),
            scores,
            reaction: [],
          };
          choice.options.push(option);
          return;
        }
        if (indented && option) {
          option.reaction.push(directiveStep(line) || lineStep(line));
          return;
        }
        closeChoice();
      }

      if (line.startsWith("@묶음")) {
        const [name, list = ""] = line.replace(/^@묶음\s*/, "").split(/[:：]/);
        const traits = list.split(",").map((s) => s.trim()).filter(Boolean);
        traits.forEach(seeTrait);
        groups.push({ name: name.trim(), traits });
        return;
      }
      if (line.startsWith("@순서")) {
        line
          .replace(/^@순서\s*/, "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach(seeTrait);
        return;
      }
      if (line.startsWith("# ")) {
        steps.push({ type: "chapter", text: line.slice(2).trim() });
        return;
      }
      if (line.startsWith("?? ") || line.startsWith("? ")) {
        const thought = line.startsWith("??");
        choice = {
          type: "choice",
          variant: thought ? "thought" : "action",
          prompt: line.replace(/^\?\??\s*/, "").trim(),
          options: [],
        };
        return;
      }
      const direct = directiveStep(line);
      if (direct) {
        steps.push(direct);
        return;
      }
      steps.push(lineStep(line));
    });
  closeChoice();

  return { steps, traits: traitOrder, groups };
};

/** 결과를 정하는 습관 목록: 묶음이 있으면 첫 묶음, 없으면 전체 */
export const primaryTraits = ({ traits, groups }) =>
  groups && groups.length ? groups[0].traits : traits;

/** 점수 → 가장 높은 습관 (동점이면 traits 순서가 앞선 것) */
export const topTrait = (scores, traits) =>
  traits.reduce(
    (best, name) => ((scores[name] || 0) > (scores[best] || 0) ? name : best),
    traits[0]
  );

/**
 * 동점이면 '가장 최근에 점수를 얻은' 습관이 이김 (recency: 습관 → 마지막으로 점수를 얻은 순번).
 * 그래도 같으면 traits 순서.
 */
export const pickTop = (scores, traits, recency = {}) =>
  traits.reduce((best, name) => {
    const a = scores[name] || 0;
    const b = scores[best] || 0;
    if (a !== b) return a > b ? name : best;
    return (recency[name] || 0) > (recency[best] || 0) ? name : best;
  }, traits[0]);

/** 묶음별 1위를 URL 용으로: 묶음 안 순번을 "2-0" 처럼 */
export const encodeTops = (tops, groups) =>
  groups.map((g, i) => g.traits.indexOf(tops[i])).join("-");

export const decodeTops = (value, groups) => {
  if (!value || !groups.length) return null;
  const idx = value.split("-").map(Number);
  if (idx.length !== groups.length) return null;
  const tops = groups.map((g, i) => g.traits[idx[i]]);
  return tops.every(Boolean) ? tops : null;
};

/** URL 용 점수 인코딩: traits 순서대로 "3-1-0-2" */
export const encodeScores = (scores, traits) =>
  traits.map((t) => Math.max(0, Math.round(scores[t] || 0))).join("-");

export const decodeScores = (value, traits) => {
  if (!value) return null;
  const nums = value.split("-").map(Number);
  if (nums.length !== traits.length || nums.some((n) => !Number.isFinite(n))) return null;
  return Object.fromEntries(traits.map((t, i) => [t, nums[i]]));
};
